// React.
import React, { useContext, useMemo, useState } from 'react';

// Types.
import InstanceTarget from '../../../../types/instance/InstanceTarget';
import ChartPlotLine from "../../../../types/ChartPlotLine";
import Change from "../../../../types/instance/Change";
import Event from "../../../../types/instance/Event";
import AlertType from "../../../../types/instance/Alert";

// Helper.
import { getConvertedTimeToUTC, highchartsCredits } from '../../../../helpers/utils';
import api from "../../../../api/Base";
import { TimeRangeContext } from "../../../../context/TimeRangeContext";
import { chartOptionsGranularity } from "../../../../helpers/chartOptionsIncreasedGranularity";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { useLicences } from "../../../../context/LicenceContext";
import Helper from "../../../../helpers/Helper";
import ConditionalRender from "../../../../helpers/ConditionalRender";
import { CHART_DATA_TYPE } from "../../../../component/Constants";
import WidgetCard from "../../../../component/instance/widget";

// Components.
import JobExecutions from "./JobExecutions";
import Jobs from "./Jobs";

// Styles
import './AgentServerPanel.scss';

export default function AgentJobs(props: {
    instance: InstanceTarget,
    chartPlotLines: ChartPlotLine[],
    changes: Change[],
    events: Event[],
    alerts: AlertType[],
    applyPeriod: Function,
}) {
    const { instance, chartPlotLines } = props;
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh

    const [loading, setLoading] = useState<boolean>(true);
    const { licences } = useLicences();

    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[][]>([]);
    const [summary, setSummary] = useState<any>(null);

    // Job filter state
    const [jobNames, setJobNames] = useState<{id: string, name: string}[]>([]); // Store job names
    const [selectedJobName, setSelectedJobName] = useState<string>('');
    const [jobsData, setJobsData] = useState<any[]>([]); // Store full jobs data to pass to Jobs tab

    const [activeTab, setActiveTab] = useState<'executions' | 'jobs'>('executions');

    const succeededPeak = useMemo(() => {
        return Helper.getPeakValue((executionsSeries || []).map(i => i[1]))
    }, [executionsSeries])

    const failedPeak = useMemo(() => {
        return Helper.getPeakValue((timeSeries || []).map(i => i[1]))
    }, [timeSeries])

    const combinedPeak = useMemo(() => {
        const allValues = [
            ...(executionsSeries?.map(item => item[1]) || []),
            ...(timeSeries?.map(item => item[1]) || [])
        ];
        return Helper.getPeakValue(allValues);
    }, [executionsSeries, timeSeries])

    void useMemo(async () => {
        try {
            const response = await api.get(`job/history/job?${timeRangeContext.getTimeRangeQueryString()}&id=${instance.id}`);

            if (response.data && Array.isArray(response.data)) {
                setJobsData(response.data);

                // Extract unique jobs from the response for the filter dropdown
                const jobList = response.data
                        .filter((job: any) => job.jobname && job.jobname.trim() !== '')
                        .map((job: any) => ({
                            id: job.jobname,  // Use jobname as ID since this endpoint doesn't have jobid
                            name: job.jobname
                        }));

                // Remove duplicates by jobname
                const uniqueJobs = Array.from(
                    new Map(jobList.map((job: any) => [job.id, job])).values()
                ).sort((a: any, b: any) => a.name.localeCompare(b.name));
                
                setJobNames(uniqueJobs);
            }
        } catch (error) {
            console.error('Failed to retrieve job names:', error);
            setJobNames([]);
            setJobsData([]);
        }
    }, [timeRangeContext, instance.id, instance.type]);

    void useMemo(async () => {
        try {
            setLoading(true);

            const jobNameParam = selectedJobName ? `&jobname=${encodeURIComponent(selectedJobName)}` : '';
            const response = await api.get(`job/history/time?${timeRangeContext.getTimeRangeQueryString()}&id=${instance.id}${jobNameParam}`);

            if (response.data && Array.isArray(response.data)) {
                const successSeries: number[][] = [];
                const failureSeries: number[][] = [];

                response.data.forEach((item: any) => {
                    const timestamp = getConvertedTimeToUTC(item);
                    const successCount = item.succeeded || item.success_count || item.successful_jobs || item.successful || 0;
                    const failureCount = item.failed || item.failure_count || item.failed_jobs || item.failures || 0;

                    successSeries.push([timestamp, successCount]);
                    failureSeries.push([timestamp, failureCount]);
                });

                setExecutionsSeries(successSeries);
                setTimeSeries(failureSeries);
            }

            setLoading(false);
        } catch (error) {
            console.error('Failed to retrieve job history time data:', error);
            setExecutionsSeries([]);
            setTimeSeries([]);
            setLoading(false);
        }
    }, [timeRangeContext, instance.id, instance.type, selectedJobName]);

    // Fetch summary data
    void useMemo(async () => {
        try {
            const jobNameParam = selectedJobName ? `&jobname=${encodeURIComponent(selectedJobName)}` : '';
            const summaryResponse = await api.get(`job/history/summary?${timeRangeContext.getTimeRangeQueryString()}&id=${instance.id}${jobNameParam}`);

            setSummary(Array.isArray(summaryResponse.data) && summaryResponse.data.length > 0 ? summaryResponse.data[0] : null);
        } catch (error) {
            console.error('Failed to retrieve job history summary data:', error);
        }
    }, [timeRangeContext, instance.id, instance.type, selectedJobName]);

    const timeChart = useMemo(() => {
        // Check if we have data before creating the chart
        const hasSuccessData = executionsSeries && executionsSeries.length > 0;
        const hasFailureData = timeSeries && timeSeries.length > 0;

        if (!hasSuccessData && !hasFailureData) {
            return <div className="text-center text-muted mt-3">
                <p>No job execution data available for the selected time range.</p>
            </div>;
        }

        const series = [
            {
                type: 'column',
                color: '#28a745', // Green for success
                data: executionsSeries || [],
                name: 'Succeeded Jobs',
                zIndex: 1,
                tooltip: {
                    valueSuffix: ' jobs'
                }
            },
            {
                type: 'column',
                color: '#dc3545', // Red for failures  
                data: timeSeries || [],
                name: 'Failed Jobs',
                zIndex: 1,
                tooltip: {
                    valueSuffix: ' jobs'
                }
            }
        ];

        // Use chartOptionsGranularity like Activity TimeChart does
        const timeChartOptions = chartOptionsGranularity(props.applyPeriod, series, combinedPeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: { enabled: true },
            timeRangeContext: timeRangeContext,
            plotLines: chartPlotLines,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
                },
            },
        });

        return <HighchartsReact
                useUtc={true}
                constructorType={"chart"}
                highcharts={Highcharts}
                options={timeChartOptions}
        />;

    }, [timeRangeContext, timeSeries, executionsSeries, chartPlotLines, licences, props.applyPeriod]);

    return (
            <>


                 <div className='row'>
                     <div className='col'>
                         <div className="card mb-3 filter-panel">
                             <div className="card-header">
                                 <i className="fal fa-filter fa-fw" aria-hidden="true"/>
                                 Filter By
                                 <i className="collapse-toggle collapsed"
                                    role="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#collapseJobFilters"
                                    aria-expanded="false"
                                    aria-controls="collapseJobFilters"
                                 />
                                  {selectedJobName && (
                                          <div className="d-inline-block ms-2">
                                              <div className="filter-badge-wrapper badge bg-success text-white">
                                              <span className="filter-badge-content">
                                                  Job={selectedJobName}
                                              </span>
                                                  <i
                                                          className="fal fa-times-circle filter-clear-button"
                                                          onClick={() => setSelectedJobName('')}
                                                          title="Clear Job Filter"
                                                  />
                                              </div>
                                          </div>
                                  )}
                             </div>
                             <div id="collapseJobFilters" className="collapse">
                                <div className="card-body">
                                    <div className="d-flex">
                                        <div className="filter-groups">
                                            <div className="btn-group dropdown">
                                                <button
                                                        type="button"
                                                        className="btn btn-dropdown filter-group-button"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                >
                                                     Job
                                                     {selectedJobName && (
                                                             <span className="badge bg-info" data-tip="Applied filters count">
                                                             1
                                                         </span>
                                                     )}
                                                 </button>
                                                 <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                                     <div className="dropdown-menu-scroll">
                                                         {jobNames.map((job) => (
                                                                 <li key={job.id} onClick={() => {
                                                                     // Toggle selection - if same job clicked, clear it; otherwise select it
                                                                     setSelectedJobName(selectedJobName === job.id ? '' : job.id);
                                                                 }}>
                                                                     <input
                                                                             type="checkbox"
                                                                             className="form-check-input"
                                                                             checked={selectedJobName === job.id}
                                                                             readOnly
                                                                     />
                                                                     <span title={job.name}>{job.name}</span>
                                                                 </li>
                                                         ))}
                                                     </div>
                                                 </div>
                                            </div>
                                        </div>
                                         <button
                                                 className="btn btn-sm btn-dark btn-reset"
                                                 onClick={() => setSelectedJobName('')}
                                         >
                                            <i className="far fa-undo"/>
                                            <span>Clear</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <div className="row flexlist">
                    <div className="col flexlist-item">
                        <WidgetCard
                                name="Job Executions"
                                iconClassName="executions"
                                icon="fa-chart-line"
                                value={summary?.executions || 0}
                                cardTooltipText="The total number of job executions"
                        />
                    </div>

                    <div className="col flexlist-item">
                        <WidgetCard
                                name="Succeeded"
                                iconClassName="success"
                                icon="fa-check-circle"
                                value={summary?.succeeded || 0}
                                cardTooltipText="The number of successful job executions"
                        />
                    </div>

                    <div className="col flexlist-item">
                        <WidgetCard
                                name="Failed"
                                icon="fa-times-circle"
                                iconClassName="processor"
                                value={summary?.failed || 0}
                                cardTooltipText="The number of failed job executions"
                        />
                    </div>

                    <div className="col flexlist-item">
                        <WidgetCard
                                name="Success Rate"
                                iconClassName="time"
                                type="percent"
                                icon="fa-percentage"
                                value={summary?.successrate || 0}
                                cardTooltipText="The percentage of successful job executions"
                        />
                    </div>
                </div>

                <div className="row row-cols-1">
                    <div className="tab-pane fade show active" role="tabpanel"
                         aria-labelledby="statements-tab">

                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                                SQL Agent Job Executions
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target={"#collapse-duration"} aria-expanded="false"
                                   aria-controls={"collapse-duration"}/>
                            </div>

                            <div id={"collapse-duration"} className="card-body collapse show">
                                <ConditionalRender if={loading && !isAutorefresh}>
                                    <div className="w-100 text-center text-muted mt-3">
                                        <div className="loader spinner chartSpinner">
                                        </div>
                                        <p className="mt-3">
                                            Loading job execution data...
                                        </p>
                                    </div>
                                </ConditionalRender>
                                <ConditionalRender if={isAutorefresh || !loading}>
                                    <p className="chart-peak">
                                        <React.Fragment>Peak
                                            Succeeded: {(succeededPeak === Infinity || succeededPeak === -Infinity) ? '-' : succeededPeak.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} jobs</React.Fragment>,&nbsp;
                                        <React.Fragment>Peak
                                            Failed: {(failedPeak === Infinity || failedPeak === -Infinity) ? '-' : failedPeak.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} jobs</React.Fragment>
                                    </p>
                                    <br/>
                                    {timeChart}
                                </ConditionalRender>
                            </div>
                        </div>


                        <div id="menu-inner" className="col nav nav-tabs menu-top" role="tablist">
                            <a
                                    id="job-executions-tab"
                                    data-bs-toggle="tab"
                                    href="#job-executions"
                                    className={activeTab === 'executions' ? 'active' : ''}
                                    role="tab"
                                    aria-controls="job-executions"
                                    aria-selected={activeTab === 'executions'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveTab('executions');
                                    }}
                            >
                                Job Executions
                            </a>
                            <a
                                    id="jobs-tab"
                                    data-bs-toggle="tab"
                                    href="#jobs"
                                    className={activeTab === 'jobs' ? 'active' : ''}
                                    role="tab"
                                    aria-controls="jobs"
                                    aria-selected={activeTab === 'jobs'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveTab('jobs');
                                    }}
                            >
                                Jobs
                            </a>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                             <JobExecutions
                                     instance={instance}
                                     showByDefault={activeTab === 'executions'}
                                     jobNameFilter={selectedJobName}
                             />
                              <Jobs
                                      instance={instance}
                                      showByDefault={activeTab === 'jobs'}
                                      jobsData={jobsData}
                                      jobNameFilter={selectedJobName}
                              />
                        </div>
                    </div>
                </div>
            </>
    )
}
